import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  computed,
  inject,
  Input,
  signal,
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDividerModule } from '@angular/material/divider';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Router } from '@angular/router';
import { NetworkGroupApiService } from '@fleet/api/network-group/network-group-api.service';
import { TimezoneContextService } from '@fleet/locale/timezone-context-service/timezone-context.service';
import { NetworkGroupModel, NetworkModel } from '@fleet/model';
import { NetworkGroupService } from '@fleet/network-group/network-group.service';
import { ProductConfigurationService } from '@fleet/product-configuration';
import { NetworkGroupHeaderService } from '@fleet/token/network-group-header/network-group-header.service';
import { DateTime } from 'luxon';
import { combineLatest, Observable } from 'rxjs';
import { distinctUntilChanged, filter, map, switchMap } from 'rxjs/operators';

export type NetworkGroupSwitcherMode = 'dropdown' | 'fullpage';

@Component({
  selector: 'fleet-network-group-switcher',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    MatFormFieldModule,
    MatSelectModule,
    MatIconModule,
    MatDividerModule,
    ReactiveFormsModule,
    MatMenuModule,
    MatButtonModule,
    MatTooltipModule,
  ],
  template: `
    <!-- Dropdown Mode -->
    @if (mode === 'dropdown' && (showSelector$ | async)) {
      <div class="flex items-center">
        <div
          [matMenuTriggerFor]="isLocked ? null : networkGroupMenu"
          (menuOpened)="onMenuOpened()"
          (menuClosed)="onMenuClosed()"
          [class]="
            'min-w-0 px-2 h-10 flex items-center ' +
            (isLocked ? 'cursor-not-allowed' : 'cursor-pointer')
          "
          [matTooltip]="tooltipText"
        >
          <div class="flex items-center min-w-0">
            @if ((network$ | async)?.logoUrl) {
              <img
                [src]="(network$ | async)?.logoUrl"
                class="w-7 h-7 rounded-full mr-2 shrink-0"
                alt="Network logo"
              />
            }
            <div class="min-w-0 flex flex-col leading-tight">
              <span class="font-medium truncate">
                {{
                  selectedGroup()?.displayName ||
                    attributionGroup()?.displayName ||
                    (network$ | async)?.displayName
                }}
              </span>
              @if (triggerTimezoneLabel(); as tzLabel) {
                <span class="text-xs text-secondary truncate">{{
                  tzLabel
                }}</span>
              }
            </div>
            <mat-icon
              class="ml-1 icon-size-3 shrink-0"
              [ngClass]="{
                'text-gray-400': isLocked,
              }"
              [svgIcon]="
                isLocked
                  ? 'heroicons_solid:lock-closed'
                  : 'heroicons_solid:chevron-down'
              "
            ></mat-icon>
          </div>
        </div>
        <mat-menu
          #networkGroupMenu="matMenu"
          [xPosition]="'after'"
          class="network-group-menu"
        >
          @if (network$ | async; as network) {
            <button
              mat-menu-item
              (click)="selectNetwork()"
              [class.selected]="selectedGroup() === null"
            >
              <div class="flex items-center">
                @if (network.logoUrl) {
                  <img
                    [src]="network.logoUrl"
                    class="w-6 h-6 rounded-full mr-2"
                    alt="Network logo"
                  />
                }
                <span class="font-bold text-lg">{{ network.displayName }}</span>
              </div>
            </button>
          }
          <mat-divider></mat-divider>
          <div class="group-header px-4 py-2 text-base font-semibold">
            Fleets
          </div>
          @for (group of availableGroups(); track group.networkGroupId) {
            <button
              mat-menu-item
              (click)="selectGroup(group)"
              [class.selected]="
                selectedGroup()?.networkGroupId === group.networkGroupId
              "
              class="fleet-group-item"
            >
              <div class="flex items-center justify-between gap-4 ml-8 w-full">
                <div class="flex items-center min-w-0">
                  @if (group.logoUrl) {
                    <img
                      [src]="group.logoUrl"
                      class="w-6 h-6 rounded-full mr-2 shrink-0"
                      alt="Group logo"
                    />
                  }
                  <span class="truncate">{{ group.displayName }}</span>
                </div>
                @if (showTimezoneUi() && groupClocks()[group.networkGroupId]; as clock) {
                  <span
                    class="text-xs text-secondary shrink-0 tabular-nums"
                    >{{ clock }}</span
                  >
                }
              </div>
            </button>
          }
        </mat-menu>
      </div>
    }

    <!-- Fullpage Mode -->
    @if (mode === 'fullpage') {
      <div
        class="fixed inset-0 flex items-center justify-center bg-white"
      >
        <div class="max-w-2xl w-full mx-auto p-8">
          <h1 class="text-3xl font-bold text-center mb-8">Select your Fleet</h1>
          <div class="space-y-4">
            @for (group of availableGroups(); track group.networkGroupId) {
              <button
                (click)="selectGroupAndNavigate(group)"
                class="w-full p-4 flex items-center bg-white border rounded-lg hover:bg-gray-50 transition-colors duration-150"
              >
                @if (group.logoUrl) {
                  <img
                    [src]="group.logoUrl"
                    class="w-10 h-10 rounded-full mr-4"
                    alt="Group logo"
                  />
                }
                <div class="flex-1">
                  <h3 class="text-lg font-medium">{{ group.displayName }}</h3>
                </div>
                <mat-icon
                  class="text-gray-400"
                  svgIcon="heroicons_solid:chevron-right"
                ></mat-icon>
              </button>
            }
          </div>
        </div>
      </div>
    }
  `,
  styles: [
    `
      ::ng-deep .network-group-menu {
        max-height: 80vh !important;
      }

      ::ng-deep .group-header {
        color: #666;
        background-color: #f5f5f5;
      }

      ::ng-deep .fleet-group-item {
        padding-left: 0 !important;
      }

      ::ng-deep .selected {
        background-color: rgba(0, 0, 0, 0.04);
      }

      button.mat-mdc-button {
        height: 40px;
      }

      .truncate {
        max-width: 200px;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }

      button.override-disabled-button[disabled] {
        opacity: 1 !important;
        background-color: #3f51b5 !important;
        color: red !important;
        cursor: pointer !important;
      }
    `,
  ],
})
export class NetworkGroupSwitcherComponent {
  @Input() mode: NetworkGroupSwitcherMode = 'fullpage';

  private networkGroupService = inject(NetworkGroupService);
  private networkGroupApiService = inject(NetworkGroupApiService);
  private productConfigurationService = inject(ProductConfigurationService);
  private router = inject(Router);
  private cdr = inject(ChangeDetectorRef);
  private networkGroupHeaderService = inject(NetworkGroupHeaderService);
  private destroyRef = inject(DestroyRef);
  private timezoneContextService = inject(TimezoneContextService);

  network = signal<NetworkModel>(null);
  availableGroups = signal<NetworkGroupModel[]>([]);
  selectedGroup = signal<NetworkGroupModel>(null);
  attributionGroup = signal<NetworkGroupModel>(null);
  private readonly nowMs = signal(Date.now());
  private readonly menuOpen = signal(false);

  private minuteTimeoutId: ReturnType<typeof setTimeout> | null = null;
  private minuteIntervalId: ReturnType<typeof setInterval> | null = null;

  readonly distinctTimezones = computed(() => {
    const zones = this.availableGroups()
      .map((group) => group.timezone)
      .filter(Boolean) as string[];
    return Array.from(new Set(zones));
  });

  /** Multi-TZ networks show timezone UI in the switcher. */
  readonly showTimezoneUi = computed(() => this.distinctTimezones().length > 1);

  readonly triggerTimezoneLabel = computed(() => {
    if (this.timezoneContextService.hasOverride()) {
      const zone = this.timezoneContextService.overrideZone();
      return zone ? `Viewing · ${zone}` : null;
    }
    if (!this.showTimezoneUi()) {
      return null;
    }
    const group = this.selectedGroup() || this.attributionGroup();
    return group?.timezone || this.timezoneContextService.effectiveZone();
  });

  readonly groupClocks = computed(() => {
    if (!this.showTimezoneUi() || !this.menuOpen()) {
      return {} as Record<string, string>;
    }
    const now = this.nowMs();
    const byZone = new Map<string, string>();
    const clocks: Record<string, string> = {};
    for (const group of this.availableGroups()) {
      const zone = group.timezone;
      if (!zone || !group.networkGroupId) {
        continue;
      }
      let formatted = byZone.get(zone);
      if (!formatted) {
        formatted = DateTime.fromMillis(now, { zone }).toFormat('t');
        byZone.set(zone, formatted);
      }
      clocks[group.networkGroupId] = formatted;
    }
    return clocks;
  });

  network$ = this.networkGroupService.network$;
  showSelector$: Observable<boolean>;
  isLocked: boolean;
  tooltipText: string;

  constructor() {
    this.destroyRef.onDestroy(() => this.stopMinuteTicker());

    this.networkGroupService.availableGroups$.subscribe((groups) => {
      this.availableGroups.set(groups);
    });

    this.showSelector$ = combineLatest([
      toObservable(this.availableGroups),
      this.productConfigurationService.networkConfig$,
    ]).pipe(
      map(([groups, networkConfig]) => {
        if (!groups || !networkConfig) return false;
        return groups.length > 0 && !networkConfig.networkGroupId;
      })
    );

    this.productConfigurationService.networkConfig$
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        filter((networkConfig) => networkConfig && networkConfig.networkId),
        switchMap((networkConfig) => {
          return this.networkGroupApiService.searchNetworkGroups(
            {},
            networkConfig.networkId
          );
        }),
        map((response) => response.body.data)
      )
      .subscribe((groups) => {
        this.availableGroups.set(groups);
        this.cdr.markForCheck();
      });

    this.networkGroupService.networkGroup$
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        distinctUntilChanged(
          (prev, curr) => prev?.networkGroupId === curr?.networkGroupId
        )
      )
      .subscribe((selectedGroup) => {
        if (selectedGroup) {
          const availableGroups = this.availableGroups();
          const matchingGroup = availableGroups.find(
            (g) => g.networkGroupId === selectedGroup.networkGroupId
          );

          if (matchingGroup) {
            this.selectedGroup.set(matchingGroup);
          } else {
            this.selectedGroup.set(null);
          }
        } else {
          this.selectedGroup.set(null);
        }
        this.cdr.markForCheck();
      });

    this.networkGroupService.attributionEntityAndGroup$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(([entity, attributionGroup]) => {
        this.isLocked = entity !== null;
        this.attributionGroup.set(attributionGroup);
        this.tooltipText = entity
          ? `Cannot change while viewing ${entity}`
          : '';
        this.cdr.markForCheck();
      });
  }

  onMenuOpened(): void {
    this.menuOpen.set(true);
    if (this.showTimezoneUi()) {
      this.startMinuteTicker();
    }
  }

  onMenuClosed(): void {
    this.menuOpen.set(false);
    this.stopMinuteTicker();
  }

  private startMinuteTicker(): void {
    this.stopMinuteTicker();
    this.nowMs.set(Date.now());
    const msUntilNextMinute = 60000 - (Date.now() % 60000);
    this.minuteTimeoutId = setTimeout(() => {
      this.nowMs.set(Date.now());
      this.minuteIntervalId = setInterval(() => {
        this.nowMs.set(Date.now());
      }, 60000);
    }, msUntilNextMinute);
  }

  private stopMinuteTicker(): void {
    if (this.minuteTimeoutId != null) {
      clearTimeout(this.minuteTimeoutId);
      this.minuteTimeoutId = null;
    }
    if (this.minuteIntervalId != null) {
      clearInterval(this.minuteIntervalId);
      this.minuteIntervalId = null;
    }
  }

  /**
   * Select a network group
   */
  selectGroup(group: NetworkGroupModel): void {
    this.networkGroupService.getNetworkGroup(
      group.networkGroupId,
      group.networkId,
      false,
      this.mode === 'dropdown' ? true : false
    );
  }

  /**
   * Select a network group and navigate to home
   */
  selectGroupAndNavigate(group: NetworkGroupModel): void {
    this.networkGroupHeaderService.networkGroupId.set(group.networkGroupId);
    this.router.navigate(['/']);
  }

  /**
   * Select the network (clear group selection)
   */
  selectNetwork(): void {
    this.networkGroupService.setSelectedGroup(null, false);
  }
}
